import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import moment from 'moment';

// Date-fns locales
import frLocale from 'date-fns/locale/fr';

// MUI X Date Pickers locales
import { frFR } from '@mui/x-date-pickers-pro/locales';

// MUI Material locales
import { frFR as muiFrFR } from '@mui/material/locale';
import { LocaleContext, LangConfig, LocaleKey, Languages } from './LocaleContext';

import { DEFAULT_LANG } from '@app/locales/i18n';

// Re-export types and context for convenience
export { LocaleContext } from './LocaleContext';
export type { LocaleKey, LangConfig, Languages, LocaleContextType } from './LocaleContext';

// ----------------------------------------------------------------------

// Configuration complète des langues
const LANGS: Languages = {
  fr: {
    label: 'French',
    value: 'fr',
    systemValue: muiFrFR,
    icon: 'static/icons/ic_flag_fr.svg',
    dateFns: frLocale,
    localeText: frFR.components.MuiLocalizationProvider.defaultProps.localeText
  }
};

// ----------------------------------------------------------------------

const getInitialLang = (): LangConfig => {
  const langStorage = localStorage.getItem('i18nextLng') as LocaleKey | null;
  return LANGS[langStorage || DEFAULT_LANG];
};

// ----------------------------------------------------------------------

const LocaleProvider = ({ children }: { children: ReactNode }) => {
  const { i18n, t: translate } = useTranslation();
  const [currentLang, setCurrentLang] = useState<LangConfig>(getInitialLang);

  const handleChangeLang = useCallback(
    (newLang: LocaleKey) => {
      const langConfig = LANGS[newLang];
      if (langConfig) {
        i18n.changeLanguage(newLang);
        moment.locale(newLang);
        setCurrentLang(langConfig);
      }
    },
    [i18n]
  );

  // Initialiser moment avec la langue courante au montage
  useEffect(() => {
    moment.locale(currentLang.value);
  }, [currentLang.value]);

  const contextValue = useMemo(
    () => ({
      currentLang,
      languages: LANGS,
      onChangeLang: handleChangeLang,
      translate
    }),
    [currentLang, handleChangeLang, translate]
  );

  return (
    <LocaleContext.Provider value={contextValue}>
      <LocalizationProvider
        dateAdapter={AdapterDateFns}
        adapterLocale={currentLang.dateFns}
        localeText={currentLang.localeText}
      >
        {children}
      </LocalizationProvider>
    </LocaleContext.Provider>
  );
};

export default LocaleProvider;
